import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CheckSquare, FileText, Loader2, Printer, RefreshCw } from "lucide-react";
import { api, fmtDate, rp } from "@/lib/api";

const bookingStatus = (booking) => booking.booking_status || "Menunggu konfirmasi";
const SIZES = [
  ["80mm", "Thermal 80mm (utama)"],
  ["100x150", "Label 100 × 150 mm"],
];

export default function OrderReceipts() {
  const [rows, setRows] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [printing, setPrinting] = useState(false);
  const [size, setSize] = useState("80mm");
  const [filterMode, setFilterMode] = useState("date");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [year, setYear] = useState(() => String(new Date().getFullYear()));

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bookings")
      .then((response) => setRows(response.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const selectedRows = useMemo(() => rows.filter((booking) => selectedIds.includes(booking.id)), [
    rows,
    selectedIds,
  ]);
  const visibleRows = useMemo(() => rows.filter((booking) => {
    const rentalDate = booking.rental_date || "";
    if (filterMode === "month") return rentalDate.startsWith(`${month}-`);
    if (filterMode === "year") return rentalDate.startsWith(`${year}-`);
    return !date || rentalDate === date;
  }), [date, filterMode, month, rows, year]);
  const allSelected = visibleRows.length > 0
    && visibleRows.every((booking) => selectedIds.includes(booking.id));

  const toggleReceipt = (bookingId) => {
    setSelectedIds((current) => current.includes(bookingId)
      ? current.filter((item) => item !== bookingId)
      : [...current, bookingId]);
  };

  const toggleAll = () => {
    setSelectedIds((current) => {
      const visibleIds = visibleRows.map((booking) => booking.id);
      if (allSelected) return current.filter((id) => !visibleIds.includes(id));
      return [...new Set([...current, ...visibleIds])];
    });
  };

  const printReceipts = async (bookings) => {
    if (!bookings.length || printing) return;
    const printWindow = window.open("", "_blank");
    setPrinting(true);
    try {
      const response = await api.post("/receipts/print", {
        ids: bookings.map((booking) => booking.id),
        size,
      }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      if (printWindow) {
        printWindow.location.href = url;
      } else {
        const download = document.createElement("a");
        download.href = url;
        download.download = `resi-order-${bookings.length}x-${size}.pdf`;
        download.click();
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div data-testid="order-receipts-page" className="pb-24">
      <style>{`
        .receipt-print { display: none; }
        @media print {
          body * { visibility: hidden; }
          .receipt-print, .receipt-print * { visibility: visible; }
          .receipt-print { display: block; position: absolute; inset: 0; padding: 28px; }
        }
      `}</style>

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Cetak Resi</h1>
          <p
            data-testid="receipts-description"
            className="mt-1 max-w-xl text-sm text-ink-soft"
          >
            Semua order dari Hub yang ditugaskan muncul otomatis.
            Satu order menghasilkan dua halaman:
            resi dan QR cek unit.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            data-testid="receipt-paper-size"
            value={size}
            onChange={(event) => setSize(event.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm"
          >
            {SIZES.map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <select
            data-testid="receipt-filter-mode"
            value={filterMode}
            onChange={(event) => setFilterMode(event.target.value)}
            className="h-10 rounded-md border border-input bg-white px-3 text-sm"
          >
            <option value="date">Tanggal</option>
            <option value="month">Bulan</option>
            <option value="year">Tahun</option>
          </select>
          {filterMode === "date" && (
            <input data-testid="receipt-date-filter" type="date" value={date}
              onChange={(event) => setDate(event.target.value)}
              className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
          )}
          {filterMode === "month" && (
            <input data-testid="receipt-month-filter" type="month" value={month}
              onChange={(event) => setMonth(event.target.value)}
              className="h-10 rounded-md border border-input bg-white px-3 text-sm" />
          )}
          {filterMode === "year" && (
            <input data-testid="receipt-year-filter" type="number" min="2020" max="2100"
              value={year} onChange={(event) => setYear(event.target.value)}
              className="h-10 w-24 rounded-md border border-input bg-white px-3 text-sm" />
          )}
          <button
            data-testid="refresh-order-receipts"
            onClick={load}
            className="inline-flex h-10 items-center gap-2 rounded-md border border-black/15 px-4"
          >
            <RefreshCw size={15} /> Muat ulang
          </button>
        </div>
      </div>

      {loading && (
        <div
          data-testid="order-receipts-loading"
          className="mt-8 flex items-center gap-2 text-sm text-ink-soft"
        >
          <Loader2 size={16} className="animate-spin" /> Memuat resi order...
        </div>
      )}

      {!loading && visibleRows.length === 0 && (
        <div
          data-testid="order-receipts-empty"
          className={[
            "mt-8 border border-dashed border-black/15 p-10 text-center text-sm",
            "text-ink-soft",
          ].join(" ")}
        >
          Belum ada order untuk Hub yang ditugaskan.
        </div>
      )}

      {!loading && visibleRows.length > 0 && (
        <div
          data-testid="receipt-batch-actions"
          className="mt-6 border-y border-black/10 py-4"
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <label className="inline-flex items-center gap-3 text-sm font-medium">
              <input
                data-testid="select-all-order-receipts"
                checked={allSelected}
                onChange={toggleAll}
                type="checkbox"
                className="h-4 w-4 accent-lm-red"
              />
              Centang semua resi ({visibleRows.length})
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="print-selected-order-receipts"
                disabled={selectedRows.length === 0 || printing}
                onClick={() => printReceipts(selectedRows)}
                className={[
                  "inline-flex h-10 items-center gap-2 rounded-md border",
                  "border-black/15 px-4",
                ].join(" ")}
              >
                <CheckSquare size={15} /> Cetak {selectedRows.length || ""} terpilih
              </button>
              <button
                data-testid="print-all-order-receipts"
                disabled={printing}
                onClick={() => printReceipts(visibleRows)}
                className={[
                  "inline-flex h-10 items-center gap-2 rounded-md bg-lm-red px-4 text-sm",
                  "font-semibold text-white disabled:opacity-40",
                ].join(" ")}
              >
                {printing
                  ? <Loader2 size={15} className="animate-spin" />
                  : <Printer size={15} />}
                Cetak semua resi
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        {visibleRows.map((booking) => (
          <article
            key={booking.id}
            data-testid={`order-receipt-card-${booking.id}`}
            className="border border-black/10 bg-white p-5"
          >
            <div className="flex items-start justify-between gap-3">
              <label className="flex min-w-0 items-start gap-3">
                <input
                  data-testid={`select-order-receipt-${booking.id}`}
                  checked={selectedIds.includes(booking.id)}
                  onChange={() => toggleReceipt(booking.id)}
                  type="checkbox"
                  className="mt-1 h-4 w-4 shrink-0 accent-lm-red"
                />
                <div>
                  <div className="font-semibold">{booking.booking_code}</div>
                  <div className="mt-1 text-xs text-ink-soft">
                    {booking.branch_name || "Hub belum dipilih"} · {bookingStatus(booking)}
                  </div>
                </div>
              </label>
              <FileText size={18} className="text-lm-red" />
            </div>
            <div className="mt-4 grid gap-1.5 text-sm">
              <ReceiptRow label="Customer" value={booking.customer_name || "—"} />
              <ReceiptRow label="Jadwal" value={receiptSchedule(booking)} />
              <ReceiptRow
                label="Paket"
                value={receiptPackage(booking)}
              />
              <ReceiptRow label="Total order" value={rp(booking.total_payment || 0)} />
            </div>
            <button
              data-testid={`print-order-receipt-${booking.id}`}
              disabled={printing}
              onClick={() => printReceipts([booking])}
              className={[
                "mt-5 inline-flex h-10 items-center gap-2 rounded-md bg-ink px-4",
                "text-sm text-cream",
              ].join(" ")}
            >
              <Printer size={15} /> Cetak resi
            </button>
          </article>
        ))}
      </div>

    </div>
  );
}

function ReceiptRow({ label, value }) {
  return (
    <div className="flex justify-between gap-6 border-b border-black/10 pb-1">
      <span className="text-ink-soft">{label}</span>
      <span className="text-right font-medium">{value}</span>
    </div>
  );
}

function receiptSchedule(booking) {
  return `${fmtDate(booking.rental_date, "id")} · ${booking.start_time || "—"}`;
}

function receiptPackage(booking) {
  return `${booking.package_name_snapshot || "—"} × ${booking.quantity || 0} meja`;
}